import { yupResolver } from '@hookform/resolvers/yup';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { capitalize } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { TextFormField } from '~components/FormFields/TextFormField';
import useLogin from '~hooks/useLogin/useLogin';
import { inputTypes } from '~pages/Auth/types';
import { signInWithEmailAndPasswordValidationSchema } from '~pages/Auth/validationSchemas';

import LinkToForgotPassword from './LinkToForgotPassword';

interface SignInWithPasswordFormProps {
  email: string;
  showEmailField: boolean;
}

interface SignInWithEmailAndPasswordData extends yup.InferType<
  typeof signInWithEmailAndPasswordValidationSchema
> {}

const SignInWithPasswordForm = ({
  email,
  showEmailField,
}: SignInWithPasswordFormProps) => {
  const [isEmailInputFocused, setIsEmailInputFocused] = useState(false);
  const [isPasswordInputFocused, setIsPasswordInputFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { login } = useLogin();

  const isInputFocused = useMemo(
    () => isEmailInputFocused || isPasswordInputFocused,
    [isEmailInputFocused, isPasswordInputFocused],
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<SignInWithEmailAndPasswordData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(signInWithEmailAndPasswordValidationSchema),
    defaultValues: { email },
  });

  const handleToggleInputFocus = useCallback((name: 'email' | 'password') => {
    if (name === 'email') {
      setIsEmailInputFocused((isFocused) => !isFocused);
    } else {
      setIsPasswordInputFocused((isFocused) => !isFocused);
    }
  }, []);

  const handleTogglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible((isVisible) => !isVisible);
  }, []);

  const onSubmit = async (data: SignInWithEmailAndPasswordData) => {
    setIsLoading(true);
    try {
      await login({ ...data });
    } catch {
      // Errors are handled in useLogin.
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      {showEmailField && (
        <TextFormField
          control={control}
          errors={errors}
          name={inputTypes.email}
          type={inputTypes.email}
          label={`${t('form_fields.email')}`}
          inputProps={{
            'data-test-id': inputTypes.email,
            readOnly: true,
          }}
          onFocus={() => handleToggleInputFocus('email')}
          onBlur={() => handleToggleInputFocus('email')}
        />
      )}
      <TextFormField
        control={control}
        errors={errors}
        name={inputTypes.password}
        type={isPasswordVisible ? inputTypes.text : inputTypes.password}
        label={`${t('form_fields.password')}`}
        inputProps={{ 'data-test-id': inputTypes.password }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleTogglePasswordVisibility}>
                {isPasswordVisible ? (
                  <VisibilityOff sx={{ width: 18 }} />
                ) : (
                  <Visibility sx={{ width: 18 }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
        onFocus={() => handleToggleInputFocus('password')}
        onBlur={() => handleToggleInputFocus('password')}
      />
      {errors.password && (
        <Typography variant={'body2'} color={'error'}>
          {capitalize(errors.password.message)}
        </Typography>
      )}
      <Button
        fullWidth
        color={isInputFocused ? 'primary' : 'inherit'}
        sx={isInputFocused ? undefined : { backgroundColor: 'action.disabledBackground' }}
        variant="contained"
        type="submit"
        loading={isLoading}
      >
        {t('actions.sign_in_with_password')}
      </Button>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 1,
          width: '100%',
        }}
      >
        <LinkToForgotPassword />
      </Box>
    </Box>
  );
};

export default SignInWithPasswordForm;
